import { useState } from 'react';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { payrollMinutesFromHours } from '../../lib/payrollHoursLabel';
import { CheckCircle, Calendar, DollarSign, Users, ChevronDown, User, Minus, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { PayrollRun, PayrollRunItem, Paystub } from '../../lib/payrollRuns';

interface PayrollCompletionProps {
  payrollRun: PayrollRun;
  payrollItems: PayrollRunItem[];
  paystubs: Paystub[];
  onClose: () => void;
  onViewHistory: () => void;
}

export default function PayrollCompletion({
  payrollRun,
  payrollItems,
  paystubs,
  onClose,
  onViewHistory
}: PayrollCompletionProps) {
  const [expandedPaystubs, setExpandedPaystubs] = useState<Set<string>>(new Set());
  const hoursText = usePayrollHoursText(
    payrollItems.map((item) => payrollMinutesFromHours(item.total_hours || 0)),
  );

  const togglePaystub = (resourceId: string) => {
    const newExpanded = new Set(expandedPaystubs);
    if (newExpanded.has(resourceId)) {
      newExpanded.delete(resourceId);
    } else {
      newExpanded.add(resourceId);
    }
    setExpandedPaystubs(newExpanded);
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">Payroll completed</h3>
          <p className="text-[13px] text-gray-500">Run #{payrollRun.run_number.toString().padStart(4, '0')}</p>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <Calendar className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-xs text-gray-500">Pay Period</p>
            <p className="text-sm font-semibold text-gray-900">
              {format(new Date(payrollRun.period_start_date), 'MMM d, yyyy')} -{' '}
              {format(new Date(payrollRun.period_end_date), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <DollarSign className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-xs text-gray-500">Total Paid</p>
            <p className="text-2xl font-bold text-gray-900">
              ${payrollRun.total_amount.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-xs text-gray-500">Resources Paid</p>
            <p className="text-sm font-semibold text-gray-900">
              {payrollRun.total_resources} {payrollRun.total_resources === 1 ? 'employee' : 'employees'}
            </p>
          </div>
        </div>
      </div>

      {/* Paystub Summaries */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Employee Paystubs</h4>
        <div className="space-y-2 max-h-[350px] overflow-y-auto">
          {paystubs.map((paystub) => {
            const item = payrollItems.find(i => i.resource_id === paystub.resource_id);
            if (!item) return null;
            
            return (
              <div key={paystub.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Paystub Header */}
                <button
                  onClick={() => togglePaystub(paystub.resource_id)}
                  className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-150 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{item.resource_name}</p>
                      <p className="text-xs text-gray-500">{item.resource_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Net Pay</p>
                      <p className="font-bold text-blue-600">${paystub.net_pay.toFixed(2)}</p>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        expandedPaystubs.has(paystub.resource_id) ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {/* Paystub Details */}
                {expandedPaystubs.has(paystub.resource_id) && (
                  <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                    {/* Paystub Calculation */}
                    <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-medium text-gray-600">Gross Earnings</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">${paystub.gross_earnings.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Minus className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-medium text-gray-600">Deductions</span>
                        </div>
                        <span className="text-sm font-bold text-red-600">-${paystub.total_deductions.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-semibold text-gray-900">Net Pay</span>
                        </div>
                        <span className="text-base font-bold text-blue-600">${paystub.net_pay.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {item.total_hours !== null && item.total_hours > 0 && (
                        <div>
                          <p className="text-xs text-gray-500">Total Hours</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {hoursText(payrollMinutesFromHours(item.total_hours || 0))}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-500">Compensation Type</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {item.compensation_type.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-600 mb-2">Earnings Breakdown</p>
                      <div className="grid grid-cols-2 gap-2">
                      {item.timeclock_earnings > 0 && (
                        <div className="bg-gray-50 rounded-lg p-2">
                          <p className="text-xs text-gray-600">
                            Timeclock ({item.timeclock_count})
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            ${item.timeclock_earnings.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {item.flat_rate_earnings > 0 && (
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-xs text-green-700">
                            Flat Rate ({item.flat_rate_count})
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            ${item.flat_rate_earnings.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {item.commission_earnings > 0 && (
                        <div className="bg-orange-50 rounded-lg p-2">
                          <p className="text-xs text-orange-700">
                            Commission ({item.commission_count})
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            ${item.commission_earnings.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {item.bonus_earnings > 0 && (
                        <div className="bg-pink-50 rounded-lg p-2">
                          <p className="text-xs text-pink-700">
                            Bonus ({item.bonus_count})
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            ${item.bonus_earnings.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onViewHistory}
          className="flex-1 rounded-xl bg-gray-100 py-2.5 text-[15px] font-medium text-gray-700 active:bg-gray-200 touch-manipulation"
        >
          View History
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-xl bg-blue-600 py-2.5 text-[15px] font-semibold text-white active:bg-blue-800 touch-manipulation"
        >
          Done
        </button>
      </div>
    </div>
  );
}

